import { useCallback, useEffect, useState } from 'react';
import type { Me } from '@avs/contract';
import { apiClient } from '../api/client.js';

/**
 * 当前用户（额度 + 是否激活）。全局轻量状态：组件用 useMe() 拿，
 * 任何会动额度的操作后调 refresh()。用一个 module-level 订阅广播，避免每个组件各拉一次。
 */

let cache: Me | null = null;
let inflight: Promise<Me> | null = null;
const subs = new Set<(m: Me | null) => void>();

function broadcast() { subs.forEach((s) => s(cache)); }

export async function fetchMe(force = false): Promise<Me> {
  if (cache && !force) return cache;
  if (inflight && !force) return inflight;
  inflight = apiClient.getMe().then((m) => { cache = m; inflight = null; broadcast(); return m; });
  return inflight;
}

export async function redeemAndRefresh(code: string): Promise<Me> {
  const m = await apiClient.redeemInvite({ code });
  cache = m; broadcast();
  return m;
}

export function useMe(): { me: Me | null; loading: boolean; refresh: () => void } {
  const [me, setMe] = useState<Me | null>(cache);
  const [loading, setLoading] = useState<boolean>(cache === null);

  useEffect(() => {
    const sub = (m: Me | null) => { setMe(m); setLoading(false); };
    subs.add(sub);
    if (cache === null) { void fetchMe().catch(() => setLoading(false)); }
    else { setLoading(false); }
    return () => { subs.delete(sub); };
  }, []);

  const refresh = useCallback(() => { void fetchMe(true).catch(() => {}); }, []);
  return { me, loading, refresh };
}
